import type { KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getPatientSummaryFullName } from '@/api/mappers';
import { formatDate } from '@/lib/format';
import type { PatientSummaryViewModel } from '@/models/patient';

interface MemberCandidatePickerProps {
  readonly candidates: readonly PatientSummaryViewModel[];
  readonly selectedGuid: string | null;
  readonly onSelect: (guid: string) => void;
  readonly loading: boolean;
  readonly isError: boolean;
  readonly hasQuery: boolean;
  readonly showMinLengthHint: boolean;
  readonly minQueryLen: number;
  readonly candidatesAriaLabel: string;
}

/**
 * Single-select picker for patient candidates. Renders the appropriate empty /
 * loading / error / no-results message inline, and a keyboard-navigable
 * radiogroup of selectable rows when results are present.
 */
export function MemberCandidatePicker({
  candidates,
  selectedGuid,
  onSelect,
  loading,
  isError,
  hasQuery,
  showMinLengthHint,
  minQueryLen,
  candidatesAriaLabel,
}: MemberCandidatePickerProps): ReactElement {
  const { t } = useTranslation();

  if (showMinLengthHint) return <Hint text={t('routes.family.members.addDialog.linkExisting.minLengthHint', { count: minQueryLen })} />;
  if (!hasQuery) return <Hint text={t('routes.family.members.addDialog.linkExisting.startTyping')} />;
  if (loading) return <Hint text={t('routes.family.members.addDialog.linkExisting.searching')} />;
  if (isError)
    return (
      <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-4 text-center text-xs text-destructive">
        {t('app.unknownError')}
      </p>
    );
  if (candidates.length === 0)
    return <Hint text={t('routes.family.members.addDialog.linkExisting.noResults')} />;

  const total = candidates.length;

  return (
    <div
      role="radiogroup"
      aria-label={candidatesAriaLabel}
      className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border"
    >
      {candidates.map((candidate, index) => {
        const isSelected = selectedGuid === candidate.guid;
        const isFocusable = isSelected || (selectedGuid === null && index === 0);
        const fullName =
          getPatientSummaryFullName(candidate) ??
          t('routes.family.members.addDialog.linkExisting.unnamed');
        const dob = candidate.dateOfBirth !== null ? formatDate(candidate.dateOfBirth) : null;

        return (
          <button
            key={candidate.guid}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isFocusable ? 0 : -1}
            data-candidate-guid={candidate.guid}
            onClick={() => {
              onSelect(candidate.guid);
            }}
            onKeyDown={(e) => {
              handleRowKeyDown(e, index, total, onSelect);
            }}
            className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
              isSelected ? 'bg-primary/10' : 'hover:bg-muted'
            }`}
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-foreground">{fullName}</span>
              <span className="block text-xs text-muted-foreground">
                {dob !== null
                  ? `${t('patientList.fields.dateOfBirth')}: ${dob}`
                  : t('routes.family.members.addDialog.linkExisting.noDob')}
                {candidate.primaryIdentifier !== null && (
                  <>
                    {' · '}
                    {candidate.primaryIdentifier}
                  </>
                )}
              </span>
            </span>
            {isSelected && (
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                {t('routes.family.members.addDialog.linkExisting.selected')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Hint({ text }: { readonly text: string }): ReactElement {
  return (
    <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
      {text}
    </p>
  );
}

function handleRowKeyDown(
  e: KeyboardEvent<HTMLButtonElement>,
  index: number,
  total: number,
  onSelect: (guid: string) => void,
): void {
  const nextIndex =
    e.key === 'ArrowDown'
      ? (index + 1) % total
      : e.key === 'ArrowUp'
        ? (index - 1 + total) % total
        : e.key === 'Home'
          ? 0
          : e.key === 'End'
            ? total - 1
            : -1;
  if (nextIndex < 0) return;
  e.preventDefault();
  const group = e.currentTarget.closest('[role="radiogroup"]');
  if (group === null) return;
  const items = Array.from(group.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
  const target = items[nextIndex];
  if (target === undefined) return;
  target.focus();
  const guid = target.dataset.candidateGuid;
  if (guid !== undefined) onSelect(guid);
}
